import {ChartTooltipAttrs, ChartTooltipJsxProps} from 'common/components/chart-tooltip';
import {SectionItem} from 'common/components/items-menu/types';
import {FlatSortConfig, SortConfig} from 'common/report/util/sorting/types';

export enum BarPattern {
  Striped = `striped`,
}

export type TooltipDataCallback = (
  segment: SegmentWithoutChildren,
  bar: Bar,
  barSegment: BarSegment,
) => ChartTooltipAttrs;

export interface BarSegment<ExtrasType = any> {
  /**
   * Color for the segment (hex string like #ABC123).
   * Chart will color bars automatically if not specificed (bar 1 => $chart1; bar 2 => #chart2, etc),
   * but individual overrides will be respected (so there may be overlap if you specify an incomplete set).
   * Recommendation is to either specify all or none
   */
  color?: string;

  pattern?: BarPattern;

  value: number;

  /** Label to place inside of the bar segment */
  label: string;

  /** can store arbitrary data associated with the bar/segment, will be passed through in handlers & callbacks (eg. barClick event, tooltipDataCallback) */
  extras?: ExtrasType;
}

export interface Bar<ExtrasType = any> {
  extras?: ExtrasType;

  /** Label to place next to the bar (i.e. 12.3K) */
  label?: string;

  /** Segments inside of the bar, enables ability to create stacked bars*/
  segments: Array<BarSegment<ExtrasType>>;
}

interface SegmentBase<ExtrasType> {
  segmentName: string;
  label: string;
  extras?: ExtrasType;

  // formatted label for the value of child segments - prefer this to be the unsegmented sum
  valueLabel?: string;
}

export interface SegmentWithChildren<ExtrasType = any> extends SegmentBase<ExtrasType> {
  children: Array<Segment<ExtrasType>>;
}

export interface SegmentWithoutChildren<ExtrasType = any> extends SegmentBase<ExtrasType> {
  bars: Array<Bar<ExtrasType>>;
}

// Segment has either bars OR children, not both
export type Segment<ExtrasType = any> = SegmentWithChildren<ExtrasType> | SegmentWithoutChildren<ExtrasType>;

export enum BarValueType {
  Absolute = `absolute`,
  Log = `log`,
}

export interface BarVizAttrs {
  'bar-value-type'?: BarValueType;
  'show-segment-labels'?: boolean;
  'enable-bar-click-events'?: boolean;
  'column-widths'?: ColumnWidths;
  'sort-config'?: SortConfig;
  'hide-tooltip'?: boolean;
  'download-mode'?: boolean;
}

export interface ColumnWidths {
  [columnIdx: number]: number;
}

export interface BarVizState {
  activeSortPanelIdx: number;
  hasNegativeValues: boolean;
  chartMin: number;
  chartMax: number;
  hoverTooltip: ChartTooltipJsxProps | null;
  columnWidths: ColumnWidths;

  resizeColumn: Element | null;
  resizeColumnIdx: number | null;
  resizeColumnWidth: number;

  selfWidth: number;
  selfHeight: number;

  progressivePage: number;
}

export interface BarClickEvent<ExtrasType = any> {
  pageX: number;
  pageY: number;
  offsetX: number;
  offsetY: number;
  segment: Segment<ExtrasType>;
  bar: Bar<ExtrasType>;
  barSegment: BarSegment<ExtrasType>;
}

export interface BarVizJsxAttrs {
  attrs: BarVizAttrs;
  props: {
    chartData: Segment[];
    tooltipDataCallback?: TooltipDataCallback;
  };
  on?: {
    barClick?: (ev: CustomEvent<BarClickEvent>) => void;
    columnWidthChange?: (ev: CustomEvent<{columnWidths: ColumnWidths}>) => void;
    sortingChange?: (ev: CustomEvent<{sortConfig: SortConfig}>) => void;
  };
}

export interface Header {
  label: string;
  sortOptionItems: Array<SectionItem<FlatSortConfig>>;
}
